<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Source</form-label>
            <div v-if="sourceWire && object" class="flex items-center justify-between gap-2 text-xs">
                <span class="truncate">{{ object.obj }} p{{ page.haspid }}b{{ object.haspid }}</span>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the source"
                    @click="hass.removeWire(page, sourceWire.id)">×</button>
            </div>
            <div v-else-if="sourceWire && stateTrigger" class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left hover:text-ui-active" title="Select the entity node"
                    @click="hass.select(page, 'node', sourceWire.from.node)">{{ entityId || '(no entity selected)' }} · state</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the source"
                    @click="hass.removeWire(page, sourceWire.id)">×</button>
            </div>
            <p v-else class="text-xs text-gray-300">
                Drag a wire from a UI object's events port, or from an entity's state, to this trigger.
            </p>
        </section>

        <!-- A state trigger: Home Assistant runs it when the entity's state (or attribute) changes. -->
        <template v-if="stateTrigger">
            <form-input name="To state" type="text" placeholder="any" :model-value="node.data.to ?? ''"
                @update:model-value="to => hass.updateNodeData(page, node.id, { to })" />
            <form-input name="From state" type="text" placeholder="any" :model-value="node.data.from ?? ''"
                @update:model-value="from => hass.updateNodeData(page, node.id, { from })" />
            <form-input name="Attribute" type="text" placeholder="(the state)" :model-value="node.data.attribute ?? ''"
                @update:model-value="attribute => hass.updateNodeData(page, node.id, { attribute })" />
            <p class="text-xs text-gray-400">
                Exported as a Home Assistant automation with a state trigger, for example a GPIO input of the plate
                (binary_sensor) turning on.
            </p>
        </template>

        <form-select v-if="object" name="Event" :model-value="node.data.event"
            @update:model-value="event => hass.updateNodeData(page, node.id, { event })">
            <option value="" disabled>Select an event</option>
            <option v-for="choice in eventChoices" :key="choice.event" :value="choice.event" :disabled="choice.disabled">
                {{ choice.event.replace(/_/g, ' ') }}{{ choice.disabled ? ' (in use)' : '' }}
            </option>
        </form-select>

        <section v-if="variables.length" class="space-y-1">
            <form-label>Event values</form-label>
            <ul class="space-y-0.5 text-xs text-gray-200">
                <li v-for="variable in variables" :key="variable.name">
                    <code class="text-ui-active">{{ variable.name }}</code> — {{ variable.label }}
                </li>
            </ul>
        </section>

        <HassRunList :page="page" :graph="graph" :node="node" :btnmatrix="object?.obj === 'btnmatrix'"
            empty="Drag a wire from this trigger's output to an action or a condition." />
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { getTriggerEntityId, getTriggerEventChoices, getTriggerSource, getWiresInto, isStateTrigger } from '../../hass/hassGraph.js';
import { getAutomationMetaByType } from '../../automation/schema/WidgetAutomationMeta.js';
import HassRunList from './HassRunList.vue';

/**
 * A trigger node: its source object and event, the values the event carries,
 * and what it runs — or, for a state trigger, the entity and the state change.
 */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
});
const hass = useHassFlowStore();

const sourceWire = computed(() => getWiresInto(props.graph, { node: props.node.id, port: 'source' })[0] ?? null);
const object = computed(() => {
    const uuid = getTriggerSource(props.graph, props.node.id);
    return uuid ? props.context.getObject(uuid) : null;
});
const stateTrigger = computed(() => isStateTrigger(props.graph, props.node.id));
const entityId = computed(() => getTriggerEntityId(props.graph, props.node.id));
const eventChoices = computed(() => getTriggerEventChoices(props.graph, props.node.id, props.context));
const variables = computed(() => (object.value ? getAutomationMetaByType(object.value.obj)?.runtimeVariables ?? [] : []));
</script>
